import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/configureStore";
import { updateUserLanguage } from "../../store/users/settings";
import "./locale.css";

interface Locale {
    lng: string;
    short: string;
    long: string;
    src: string;
}

interface LocaleSelectProps {
    displayFlag?: boolean;
}

const LocaleSelect = ({ displayFlag = false }: LocaleSelectProps) => {
    const { i18n, t } = useTranslation("common");
    const dispatch = useDispatch<AppDispatch>();
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const baseImageUri = process.env.PUBLIC_URL || "";
    const locales: Locale[] = useMemo(
        () => [
            {
                lng: "et",
                short: "EST",
                long: t(`common:locales.et`),
                src: `${baseImageUri}/flags/estonia.png`,
            },
            {
                lng: "en",
                short: "ENG",
                long: t(`common:locales.en`),
                src: `${baseImageUri}/flags/united-kingdom.png`,
            },
            {
                lng: "lv",
                short: "LAT",
                long: t(`common:locales.lv`),
                src: `${baseImageUri}/flags/lv.png`,
            },
            {
                lng: "lt",
                short: "LIT",
                long: t(`common:locales.lt`),
                src: `${baseImageUri}/flags/lt.png`,
            },
            {
                lng: "ru",
                short: "RUS",
                long: t(`common:locales.ru`),
                src: `${baseImageUri}/flags/ru.png`,
            },
        ],
        [baseImageUri, t]
    );

    // Track current locale
    const currentLocale = useMemo(
        () => locales.find((locale) => locale.lng === i18n.language) || locales[0],
        [i18n.language, locales]
    );

    // Handle locale change
    const handleLocaleChange = (locale: Locale) => (event: ReactMouseEvent) => {
        event.preventDefault();
        i18n.changeLanguage(locale.lng);
        dispatch(updateUserLanguage(locale.lng));
        setCollapsed(true);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setCollapsed(true);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="dropdown locale-selector d-inline-block" ref={dropdownRef}>
            <button
                className="locale-selector-header btn d-flex align-items-center visual-selection-aid"
                type="button"
                title={currentLocale.long}
                onClick={() => setCollapsed(!collapsed)}
                aria-expanded={!collapsed}
            >
                {displayFlag && (
                    <img
                        src={currentLocale.src}
                        alt={currentLocale.long}
                        className="me-2"
                        style={{ width: "20px", height: "20px" }}
                    />
                )}
                <span>{currentLocale.short}</span>
            </button>
            <ul className={`dropdown-menu${collapsed ? "" : " show"}`}>
                {locales.filter(i => i.lng !== currentLocale.lng).map((locale) => (
                    <li key={locale.lng}>
                        <button
                            type="button"
                            title={locale.long}
                            className={`dropdown-item `}
                            onClick={handleLocaleChange(locale)}
                        >
                            {displayFlag && (
                                <img
                                    src={locale.src}
                                    alt={locale.long}
                                    className="me-2"
                                    style={{ width: "20px", height: "20px" }}
                                />
                            )}
                            <span>{locale.short}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default LocaleSelect;